import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from '@mantine/core';
import { IconCheck, IconX, IconTrash } from '@tabler/icons-react';
import { API_ENDPOINTS } from '../constants/api';
import './EditOrganizationModal.css';

interface Organization {
  organization_id: string;
  organization: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile?: string;
  engagement_level: number;
  is_super_user: boolean;
}

interface EditOrganizationModalProps {
  opened: boolean;
  onClose: () => void;
  organization: Organization;
  onSave: (updatedOrg: Partial<Organization>) => Promise<void>;
  onDeleted?: () => void;
  userToken: string;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  opened,
  onClose,
  organization,
  onSave,
  onDeleted,
  userToken,
}) => {
  const [formData, setFormData] = useState({
    organization: organization.organization,
    first_name: organization.first_name,
    last_name: organization.last_name,
    mobile: organization.mobile || '',
    engagement_level: organization.engagement_level,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmationName, setConfirmationName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setFormData({
        organization: organization.organization,
        first_name: organization.first_name,
        last_name: organization.last_name,
        mobile: organization.mobile || '',
        engagement_level: organization.engagement_level,
      });
      setError(null);
      setShowDeleteConfirm(false);
      setConfirmationName('');
    }
  }, [opened, organization]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update organization',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isDeleting) {
      onClose();
    }
  };

  const confirmationMatches =
    confirmationName.trim() === organization.organization.trim();

  const handleDeleteConfirm = async () => {
    if (!confirmationMatches) {
      setError('Type the organization name exactly to confirm deletion');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.ORGANIZATIONS}/${organization.organization_id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({
            confirmation_name: confirmationName.trim(),
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Failed to delete organization',
        );
      }

      setShowDeleteConfirm(false);
      onClose();
      onDeleted?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete organization',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened && !showDeleteConfirm}
        onClose={handleClose}
        title="Edit Organization Details"
        size="md"
        closeOnClickOutside={!isLoading && !isDeleting}
        closeOnEscape={!isLoading && !isDeleting}
        className="edit-organization-modal"
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing="md">
            <TextInput
              label="Organization Name"
              value={formData.organization}
              onChange={(e) =>
                handleInputChange('organization', e.target.value)
              }
              required
              disabled={isLoading || isDeleting}
            />

            <TextInput
              label="Contact First Name"
              value={formData.first_name}
              onChange={(e) =>
                handleInputChange('first_name', e.target.value)
              }
              required
              disabled={isLoading || isDeleting}
            />

            <TextInput
              label="Contact Last Name"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              required
              disabled={isLoading || isDeleting}
            />

            <TextInput
              label="Mobile Phone"
              value={formData.mobile}
              onChange={(e) => handleInputChange('mobile', e.target.value)}
              placeholder="Enter mobile phone number"
              disabled={isLoading || isDeleting}
            />

            <TextInput
              label="Email Address"
              value={organization.email}
              disabled
              description="Email address cannot be changed as it's used for login"
            />

            {error && (
              <Text color="red" size="sm">
                {error}
              </Text>
            )}

            <Group position="apart" spacing="md">
              <Button
                type="button"
                color="red"
                variant="outline"
                onClick={() => {
                  setError(null);
                  setConfirmationName('');
                  setShowDeleteConfirm(true);
                }}
                disabled={isLoading || isDeleting}
                leftIcon={<IconTrash size={16} />}
              >
                Delete Organization
              </Button>
              <Group spacing="md">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading || isDeleting}
                  leftIcon={<IconX size={16} />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={isDeleting}
                  leftIcon={<IconCheck size={16} />}
                >
                  Save Changes
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={opened && showDeleteConfirm}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteConfirm(false);
            setConfirmationName('');
            setError(null);
          }
        }}
        title="Delete Organization"
        size="md"
        closeOnClickOutside={!isDeleting}
        closeOnEscape={!isDeleting}
        className="edit-organization-modal"
      >
        <Stack spacing="md">
          <Alert color="red" title="This cannot be undone">
            Deleting <strong>{organization.organization}</strong> permanently
            removes all business units, farms, fields, crop records, tillage /
            fertilizer / manure data, carbon results, evidence files, and METI
            registrations for this organization.
          </Alert>

          <Text size="sm">
            Type the organization name <strong>{organization.organization}</strong>{' '}
            to confirm.
          </Text>

          <TextInput
            label="Organization Name"
            value={confirmationName}
            onChange={(e) => setConfirmationName(e.target.value)}
            placeholder={organization.organization}
            disabled={isDeleting}
            data-autofocus
          />

          {error && (
            <Text color="red" size="sm">
              {error}
            </Text>
          )}

          <Group position="right" spacing="md">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setConfirmationName('');
                setError(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDeleting}
              disabled={!confirmationMatches}
              leftIcon={<IconTrash size={16} />}
              onClick={handleDeleteConfirm}
            >
              Delete Permanently
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default EditOrganizationModal;
